import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';

import { Box, Stack, Dialog, Typography, IconButton } from '@mui/material';

import fileDownload from 'src/utils/fileDownload';

import { getFileByName } from 'src/services/file.service';

import Iconify from 'src/components/iconify';

import { styles } from '../../styles';

export default function MediaMessage({ message, isPrimary, ticketId, isLastInGroup }) {
  const { t } = useTranslation();
  const [imgUrl, setImgUrl] = useState(null);
  const [isImage, setIsImage] = useState(false);
  const [openPreview, setOpenPreview] = useState(false); // Controls the Lightbox

  const rawPath = message.split(/[\\/]/).pop();
  const fileName = rawPath;
  const extension = fileName.split('.').pop().toLowerCase();

  const displayName = fileName.replace(/_\d{2}_\d{2}_\d{4}.*(?=\.[^.]+$)/, '');

  const getIconPath = (ext) => {
    const map = {
      pdf: 'pdf',
      xlsx: 'excel',
      xls: 'excel',
      csv: 'excel',
      docx: 'word',
      doc: 'word',
      pptx: 'power_point',
      ppt: 'power_point',
      zip: 'zip',
      rar: 'zip',
      '7z': 'zip',
      gz: 'zip',
      txt: 'text',
      json: 'text',
      jpg: 'image',
      jpeg: 'image',
      png: 'image',
      gif: 'image',
      webp: 'image',
      svg: 'image',
    };
    return `/assets/icons/Media/${map[ext] || 'text'}.svg`;
  };

  useEffect(() => {
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'];
    let activeUrl = null;

    if (imageExtensions.includes(extension)) {
      setIsImage(true);
      getFileByName(fileName, ticketId)
        .then((blob) => {
          activeUrl = URL.createObjectURL(blob);
          setImgUrl(activeUrl);
        })
        .catch((err) => console.error('Preview failed', err));
    }
    return () => {
      if (activeUrl) URL.revokeObjectURL(activeUrl);
    };
  }, [fileName, extension, ticketId]);

  const handleDownload = async (e) => {
    if (e) e.stopPropagation(); // Prevent opening preview when clicking download
    try {
      const fileData = await getFileByName(fileName, ticketId);
      await fileDownload(fileData, fileName);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  // Logic: Tail only for the last message in a consecutive group
  const radius = !isPrimary
    ? isLastInGroup
      ? '1px 18px 18px 18px'
      : '18px 18px 18px 18px'
    : '18px 18px 18px 18px';

  if (isImage) {
    return (
      <>
        <Box
          onClick={() => setOpenPreview(true)} // Opens the Lightbox
          sx={{
            position: 'relative',
            borderRadius: '16px',
            overflow: 'hidden',
            width: 'fit-content',
            maxWidth: '320px',
            cursor: 'pointer', // Indicates it can be opened
            border: '1px solid rgba(0,0,0,0.1)',
            boxShadow: '0px 8px 24px rgba(0,0,0,0.12)',
            lineHeight: 0,
            transition: 'transform 0.2s',
            '&:hover': { transform: 'scale(1.02)' },
            '&:hover .download-overlay': { opacity: 1, transform: 'translateY(0)' },
          }}
        >
          {imgUrl ? (
            <Box
              component="img"
              src={imgUrl}
              sx={{ width: '100%', height: 'auto', display: 'block' }}
            />
          ) : (
            <Box
              sx={{ p: 4, bgcolor: '#F4F6F8', display: 'flex', justifyContent: 'center' }}
            >
              <Iconify icon="solar:gallery-bold" width={32} />
            </Box>
          )}

          <Box
            className="download-overlay"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '60px',
              background:
                'linear-gradient(to bottom, rgba(0,0,0,0.6) 0%, transparent 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              px: 1.5,
              opacity: 0,
              transform: 'translateY(-10px)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            <IconButton
              onClick={handleDownload}
              sx={{
                color: '#FFF',
                bgcolor: 'rgba(255,255,255,0.2)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.4)' },
              }}
            >
              <Iconify icon="solar:download-bold" width={24} />
            </IconButton>
          </Box>
        </Box>

        {/* FULLSCREEN PREVIEW DIALOG */}
        <Dialog
          fullScreen
          open={openPreview}
          onClose={() => setOpenPreview(false)}
          PaperProps={{
            sx: {
              bgcolor: 'rgba(0,0,0,0.9)',
              boxShadow: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            },
          }}
        >
          {/* EXIT BUTTON */}
          <IconButton
            onClick={() => setOpenPreview(false)}
            sx={{
              position: 'absolute',
              top: 20,
              right: 20,
              color: '#FFF',
              bgcolor: 'rgba(255,255,255,0.1)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
            }}
          >
            <Iconify icon="solar:close-circle-bold" width={32} />
          </IconButton>

          {/* PREVIEW IMAGE */}
          <Box
            component="img"
            src={imgUrl}
            sx={{
              maxWidth: '95%',
              maxHeight: '90%',
              objectFit: 'contain',
              borderRadius: '8px',
              boxShadow: '0 0 40px rgba(0,0,0,0.5)',
            }}
          />

          {/* BOTTOM FILENAME & DOWNLOAD INFO */}
          <Box
            sx={{ position: 'absolute', bottom: 30, textAlign: 'center', width: '100%' }}
          >
            <Typography variant="h6" sx={{ color: '#FFF', mb: 1, fontWeight: 700 }}>
              {displayName}
            </Typography>
            <IconButton
              onClick={handleDownload}
              sx={{
                color: '#1B0DE4',
                bgcolor: '#FFF',
                '&:hover': { bgcolor: '#F4F6F8' },
              }}
            >
              <Iconify icon="solar:download-bold" width={28} />
            </IconButton>
          </Box>
        </Dialog>
      </>
    );
  }

  return (
    <Box
      sx={{
        ...styles.bubbleBase,
        bgcolor: isPrimary ? '#B9DAFF' : '#E4E6EB',
        borderRadius: radius,
        p: '12px 20px',
        minWidth: '280px',
        border: '1px solid rgba(0,0,0,0.08)',
        boxShadow: '0px 4px 10px rgba(0,0,0,0.04)',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            bgcolor: 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Box
            component="img"
            src={getIconPath(extension)}
            sx={{ width: 38, height: 38, display: 'block' }}
          />
        </Box>

        <Box
          sx={{
            flexGrow: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <Typography
            variant="subtitle2"
            noWrap
            sx={{
              fontWeight: 900,
              color: '#1C1B1F',
              fontSize: '0.875rem',
              lineHeight: 1.2,
              mb: 0.3,
            }}
          >
            {displayName}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: isPrimary ? '#103996' : '#637381',
              fontWeight: 800,
              textTransform: 'uppercase',
              fontSize: '0.65rem',
              letterSpacing: '0.03em',
              lineHeight: 1,
            }}
          >
            {extension} {t('ticket.files')}
          </Typography>
        </Box>

        <IconButton
          size="small"
          onClick={handleDownload}
          sx={{
            bgcolor: isPrimary ? 'rgba(27, 13, 228, 0.08)' : 'rgba(0,0,0,0.05)',
            color: '#000000',
            width: 32,
            height: 32,
            '&:hover': {
              bgcolor: isPrimary ? 'rgba(27, 13, 228, 0.15)' : 'rgba(0,0,0,0.1)',
            },
          }}
        >
          <Iconify icon="solar:download-bold" width={20} />
        </IconButton>
      </Stack>
    </Box>
  );
}

MediaMessage.propTypes = {
  message: PropTypes.string,
  isPrimary: PropTypes.bool,
  ticketId: PropTypes.any,
  isLastInGroup: PropTypes.bool,
};
